import React from 'react';
import { Field, Formik, Form } from 'formik';
import PropTypes from 'prop-types';
import { get, isNil } from 'lodash';
import { Spinner, SpinnerSize } from 'office-ui-fabric-react/lib/Spinner';
import { DefaultButton, Stack, Text, PrimaryButton } from 'office-ui-fabric-react';
import PageTitle from '../PageTitle';
import StackGrid from '../StackGrid';
import styles from './UnassignedRoyaltyFeeForm.style';
import Dropdown from '../../components/Dropdown';
import { getCalcParamsText } from '../../constants';
import TextField from '../../components/TextField';
import TextButton from '../TextButton';

const UnassignedRoyaltyFeeForm = ({
  calculationTypes,
  fallbackAgreement,
  onSubmit,
  onCancel,
  isCreatingFallbackAgreement,
}) => {
  const initialValues = {
    calculationTypes,
    fallbackAgreement,
  };

  function getCalcParams(calculationType) {
    const selectedCalculation = calculationTypes.data?.find(
      calcType => calcType.slug === calculationType,
    );

    if (!selectedCalculation) {
      return [];
    }

    return selectedCalculation.calcParams.map(calcParam => ({
      calcParam,
      title: getCalcParamsText(calcParam),
    }));
  }

  // const submitHandler = async values => {

  //   await onSubmit(formValues);
  // };

  const validate = formValues => {
    const fallbackAgreementObj = formValues.fallbackAgreement;
    const errors = {};

    const selectedCalculation = calculationTypes.data?.find(
      calcType => calcType.slug === fallbackAgreementObj?.calculation,
    );

    if (!fallbackAgreementObj?.calculation || !selectedCalculation) {
      errors['fallbackAgreement.calculation'] = 'Required';
    } else {
      selectedCalculation?.calcParams.forEach(calcParam => {
        if (isNil(get(fallbackAgreementObj, `calcParams.${calcParam}`))) {
          errors[`fallbackAgreement.calcParams.${calcParam}`] = 'Required';
        } else if (fallbackAgreementObj.calcParams[calcParam] < 0) {
          errors[`fallbackAgreement.calcParams.${calcParam}`] = 'Value should be greater than 0';
        } else if (fallbackAgreementObj.calcParams[calcParam] === '') {
          errors[`fallbackAgreement.calcParams.${calcParam}`] = 'Required';
        } else if (
          ['percentagePrice', 'percentageRevenue'].includes(calcParam) &&
          fallbackAgreementObj.calcParams[calcParam] > 100
        ) {
          errors[`fallbackAgreement.calcParams.${calcParam}`] = 'Value should less than 100';
        }
      });
    }

    return errors;
  };

  return (
    <Formik
      initialValues={initialValues}
      validateOnChange={false}
      validateOnBlur={false}
      validate={validate}
      onSubmit={onSubmit}
    >
      {({ values, submitForm }) => (
        <Form>
          <PageTitle>Royalty Fee for Unassigned</PageTitle>
          {values.calculationTypes.error && (
            <Stack horizontal={false}>
              <Text className={styles.errorText}>Calculation Methods Error</Text>
              <DefaultButton onClick={() => values.calculationTypes.fetchApi()}>
                Try Again
              </DefaultButton>
            </Stack>
          )}

          {!values.calculationTypes.error && (
            <StackGrid smCols={1} mdCols={3} lgCols={3} shrink className={styles.unassignedForm}>
              <Field
                name="fallbackAgreement.calculation"
                component={Dropdown}
                label="Calculation Method"
                placeholder={values.calculationTypes.isLoading ? 'Loading...' : 'Select'}
                options={values.calculationTypes.data || []}
                disabled={isCreatingFallbackAgreement}
                data-testid="agreementCalculationType"
                required
              />

              {getCalcParams(values.fallbackAgreement?.calculation).map(({ calcParam, title }) => (
                <Field
                  key={calcParam}
                  name={`fallbackAgreement.calcParams.${calcParam}`}
                  component={TextField}
                  label={title}
                  disabled={isCreatingFallbackAgreement}
                  required
                  type="number"
                  data-testid="agreementCalcParam"
                />
              ))}
            </StackGrid>
          )}

          <div className={styles.buttonContainer}>
            <TextButton onClick={onCancel}>Cancel</TextButton>
            <PrimaryButton
              disabled={isCreatingFallbackAgreement}
              onClick={submitForm}
              data-testid="save-fallback-agreement-button"
            >
              Save and Continue
              {isCreatingFallbackAgreement && (
                <Spinner
                  data-testid="create-fallback-agreement-spinner"
                  size={SpinnerSize.medium}
                  className={styles.loadingSpinner}
                />
              )}
            </PrimaryButton>
          </div>
        </Form>
      )}
    </Formik>
  );
};

UnassignedRoyaltyFeeForm.propTypes = {
  calculationTypes: PropTypes.object.isRequired,
  fallbackAgreement: PropTypes.object,
  onSubmit: PropTypes.func,
  onCancel: PropTypes.func,
  isCreatingFallbackAgreement: PropTypes.bool,
};

UnassignedRoyaltyFeeForm.defaultProps = {
  fallbackAgreement: undefined,
  onSubmit: () => {},
  onCancel: () => {},
  isCreatingFallbackAgreement: false,
};

export default UnassignedRoyaltyFeeForm;
